import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { RevealBoundaryContext } from './animation-schedule-context';

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

export function RevealBoundary({ children = null, isAnimationDisabled = false }) {
  const boundaryRef = useRef(null);
  const contentRef = useRef(null);
  const revealedLeavesRef = useRef(new Set());
  const animationFrameRef = useRef(null);
  const isRestricted = !isAnimationDisabled;

  const cancelMeasurement = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const measure = useCallback(() => {
    const boundary = boundaryRef.current;
    const content = contentRef.current;

    if (!isRestricted || !boundary || !content) {
      return;
    }

    const boundaryRect = boundary.getBoundingClientRect();
    const boundaryTop = isFiniteNumber(boundaryRect.top) ? boundaryRect.top : 0;
    let lowestBottom = boundaryTop;

    revealedLeavesRef.current.forEach(leaf => {
      if (!leaf?.isConnected || !content.contains(leaf)) {
        revealedLeavesRef.current.delete(leaf);
        return;
      }

      const computedStyle = window.getComputedStyle?.(leaf);
      if (computedStyle?.visibility === 'hidden') {
        return;
      }

      const leafRect = leaf.getBoundingClientRect();
      if (isFiniteNumber(leafRect.bottom)) {
        lowestBottom = Math.max(lowestBottom, leafRect.bottom);
      }
    });

    boundary.style.height = `${Math.max(0, Math.ceil(lowestBottom - boundaryTop))}px`;
  }, [isRestricted]);

  const scheduleMeasurement = useCallback(() => {
    if (!isRestricted || animationFrameRef.current !== null) {
      return;
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = null;
      measure();
    });
  }, [isRestricted, measure]);

  const reportRevealLeaf = useCallback(
    element => {
      if (element) {
        revealedLeavesRef.current.add(element);
      }

      scheduleMeasurement();
    },
    [scheduleMeasurement]
  );

  useLayoutEffect(() => {
    const boundary = boundaryRef.current;
    const content = contentRef.current;
    const revealedLeaves = revealedLeavesRef.current;

    if (!isRestricted || !boundary || !content) {
      cancelMeasurement();
      revealedLeaves.clear();
      if (boundary) {
        boundary.style.removeProperty('height');
        boundary.style.removeProperty('overflow');
      }
      return undefined;
    }

    boundary.style.height = '0px';
    boundary.style.overflow = 'hidden';

    const resizeObserver = new ResizeObserver(scheduleMeasurement);

    resizeObserver?.observe(content);
    scheduleMeasurement();

    return () => {
      resizeObserver?.disconnect();
      cancelMeasurement();
      revealedLeaves.clear();
      boundary.style.removeProperty('height');
      boundary.style.removeProperty('overflow');
    };
  }, [cancelMeasurement, isRestricted, scheduleMeasurement]);

  const contextValue = useMemo(
    () => ({
      reportRevealLeaf,
    }),
    [reportRevealLeaf]
  );

  return (
    <RevealBoundaryContext.Provider value={contextValue}>
      <div
        ref={boundaryRef}
        className="animation-reveal-boundary"
        style={isRestricted ? { height: '0px', overflow: 'hidden' } : undefined}
      >
        <div ref={contentRef} className="animation-reveal-boundary-content">
          {children}
        </div>
      </div>
    </RevealBoundaryContext.Provider>
  );
}

RevealBoundary.propTypes = {
  children: PropTypes.node,
  isAnimationDisabled: PropTypes.bool,
};
